import {
  Button,
  HStack,
  IconButton,
  Spacer,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { isAdminDashboard } from 'packages/common-frontend/utils';
import { useUploadContext } from 'packages/dataPipeline/hooks/UploadContext';
import { useNodeProgress } from 'packages/dataPipeline/hooks/useNodeProgress';
import { SectionHeader } from 'packages/dataPipeline/ui/components/SectionHeader';

const AssignmentActionStatus = ({ artifactId }: { artifactId: string }) => {
  const navigate = useNavigate();

  const nodeProgress = useNodeProgress({ artifactId });
  const successColor = useColorModeValue(colors.green[400], colors.green[300]);

  if (!nodeProgress.status) {
    // node status is not available.
    // if all the steps before are complete, then its time to show assignment link.
    if (nodeProgress.complete === nodeProgress.total) {
      return (
        <Button
          colorScheme="v2-aurora"
          variant="secondary"
          onClick={() => {
            if (!isAdminDashboard()) {
              navigate('/assignments/');
            }
          }}
        >
          Confirm Assignment
        </Button>
      );
    }
    // show the progress of the steps.
    return (
      <Text>
        {nodeProgress.complete} of {nodeProgress.total} steps complete
      </Text>
    );
  }

  // status is available for the assignment node
  // that means assignment is already done
  // show complete checkbox.
  return (
    <HStack>
      <IconButton
        aria-label="statusIcon"
        icon={<FaIcon color={successColor} icon="check-circle" size="16px" />}
        size="md"
        variant="v2-ghost"
      />
      <Text>Confirmed</Text>
    </HStack>
  );
};

const AssignmentAction = ({ artifactId }: { artifactId: string }) => {
  const textColor = useColorModeValue(colors.black[70], colors.white[700]);
  const backgroundColor = useColorModeValue(
    colors.background[50],
    colors.gray[700],
  );
  const borderColor = useColorModeValue(colors.gray[100], '#ffffff29');

  return (
    <VStack
      as="span"
      alignItems="flex-start"
      spacing={1}
      borderRadius="8px"
      padding="16px"
      border={`1px solid ${borderColor}`}
      bg={backgroundColor}
      width="100%"
    >
      <HStack width="100%" justifyContent="space-between">
        <Text
          as="span"
          color={textColor}
          fontSize="md"
          fontWeight="bold"
          lineHeight="1.2rem"
        >
          Assignment
        </Text>
        <Spacer />
        <AssignmentActionStatus artifactId={artifactId} />
      </HStack>
    </VStack>
  );
};

export const AssignmentActions = () => {
  const uploadContext = useUploadContext();

  const assignments =
    uploadContext.completeRecord?.meshDocument.assignments || [];

  return (
    <>
      {assignments.map((assignment) => {
        return (
          <React.Fragment key={assignment.artifactId}>
            <SectionHeader title="Assign Receivables" />
            <AssignmentAction artifactId={assignment.artifactId} />
          </React.Fragment>
        );
      })}
    </>
  );
};
